import React, { useEffect, useRef, useState } from 'react'
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Header, KeyboardAwareView, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import {
  defaultEventFormState,
  eventAccessProblem,
  eventToFormState,
  translateApiError,
  validateEventForm,
  type EventFormState,
} from '@utils'
import {
  useCreateEvent,
  useDeleteEvent,
  useEventById,
  useMyEvents,
  useUpdateEvent,
} from '@hooks'
import { AccessDenied } from '../components/AccessDenied'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import {
  ContactSection,
  CoverSection,
  DatesSection,
  InfoSection,
  PlaceSection,
  RegistrationSection,
  VisibilitySection,
  TicketingSection,
  type SectionProps,
} from './components/EventFormSections'

type Props = NativeStackScreenProps<EventsStackParamList, 'EventCreate' | 'EventEdit'>

/** Create (`/events/new`) or edit (`/events/:eventId/edit`) an event. */
export const EventForm: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const eventId =
    route.name === 'EventEdit' ? (route.params as { eventId: string }).eventId : undefined
  const isEdit = Boolean(eventId)

  const { data: existing, isFetchedAfterMount, error: loadError } = useEventById(eventId)
  const myBilling = useMyEvents().data?.billing ?? null
  const createEvent = useCreateEvent()
  const updateEvent = useUpdateEvent()
  const deleteEvent = useDeleteEvent()

  const [values, setValues] = useState<EventFormState>(() => defaultEventFormState())
  const [errors, setErrors] = useState<SectionProps['errors']>({})
  const [apiError, setApiError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  /** Values loaded from the API, to only send the registration deadline when touched. */
  const initial = useRef<EventFormState | undefined>(undefined)
  const scrollRef = useRef<ScrollView>(null)
  const loadedFor = useRef<string | undefined>(undefined)

  useEffect(() => {
    // Wait for the fresh answer (the cache may hold an older copy), then fill the
    // form once per event: a refetch arriving while the organizer is typing (or
    // has just picked a chip) must not wipe their edits.
    if (!existing || !isFetchedAfterMount) return
    if (loadedFor.current === existing.event.id) return
    loadedFor.current = existing.event.id
    const loaded = eventToFormState(existing.event)
    initial.current = loaded
    setValues(loaded)
  }, [existing, isFetchedAfterMount])

  const onChange: SectionProps['onChange'] = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }))
    // The message is stale as soon as the field is edited; submit re-validates.
    setErrors((current) => Object.fromEntries(Object.entries(current).filter(([k]) => k !== key)))
  }

  const submit = () => {
    const result = validateEventForm(values, { initial: initial.current })
    const translated: SectionProps['errors'] = {}
    for (const [field, code] of Object.entries(result.errors)) {
      translated[field as keyof EventFormState] = t(`events.form.errors.${code}`)
    }
    setErrors(translated)
    const { payload } = result
    if (!payload) {
      scrollRef.current?.scrollTo({ y: 0, animated: true })
      return
    }
    setApiError('')
    const onError = (error: unknown) => setApiError(translateApiError(t, error).message)
    if (eventId) {
      updateEvent.mutate(
        { eventId, values: payload },
        { onSuccess: () => navigation.navigate('EventManage', { eventId }), onError },
      )
      return
    }
    createEvent.mutate(payload, {
      onSuccess: ({ event }) => navigation.replace('EventManage', { eventId: event.id }),
      onError,
    })
  }

  // Edit: the fee the event page shows; create: the organizer's own fee.
  const billing =
    existing?.event.commissionRateBps !== undefined && existing.event.feePaidBy
      ? { commissionRateBps: existing.event.commissionRateBps, feePaidBy: existing.event.feePaidBy }
      : myBilling
  const sectionProps: SectionProps = { values, errors, onChange, billing }
  const accessProblem = isEdit ? eventAccessProblem(existing, loadError) : null
  const busy = createEvent.isPending || updateEvent.isPending || deleteEvent.isPending

  if (accessProblem) {
    return (
      <AccessDenied
        message={t(`events.access.${accessProblem === 'notFound' ? 'notFound' : 'forbidden'}`)}
        onBack={() => navigation.navigate('EventsList')}
      />
    )
  }

  return (
    <Screen>
      <Header
        title={isEdit ? t('events.form.editTitle') : t('events.form.createTitle')}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        onPressLeft={() =>
          navigation.canGoBack() ? navigation.goBack() : navigation.navigate('EventsList')
        }
      />
      <KeyboardAwareView isFullScreen androidOnly offsetTopInset>
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={styles.scroll}
          // Native: a tap on a venue suggestion must land even with the keyboard up,
          // and the focused field must stay above the keyboard.
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets
        >
          <InfoSection {...sectionProps} />
          <DatesSection {...sectionProps} />
          <PlaceSection {...sectionProps} />
          <VisibilitySection {...sectionProps} />
          <RegistrationSection {...sectionProps} />
          <TicketingSection {...sectionProps} />
          <CoverSection {...sectionProps} />
          <ContactSection {...sectionProps} />

          <View style={styles.footer}>
            {apiError ? (
              <Text type="bodySmall" color={theme.colors.error} testID="event-api-error">
                {apiError}
              </Text>
            ) : null}
            <View style={styles.actions}>
              <Button
                title={isEdit ? t('events.form.save') : t('events.form.create')}
                disabled={busy}
                onPress={submit}
              />
            </View>
          </View>

          {isEdit && eventId ? (
            <View style={styles.danger}>
              {confirmDelete ? (
                <>
                  <Text type="body">{t('events.form.deleteConfirm')}</Text>
                  <View style={styles.row}>
                    <Button
                      title={t('events.form.delete')}
                      isRed
                      isSmall
                      disabled={busy}
                      onPress={() =>
                        deleteEvent.mutate(eventId, {
                          onSuccess: () => navigation.navigate('EventsList'),
                        })
                      }
                    />
                    <Button
                      title={t('events.form.cancel')}
                      unfilled
                      isSmall
                      onPress={() => setConfirmDelete(false)}
                    />
                  </View>
                </>
              ) : (
                <TouchableOpacity accessibilityRole="button" onPress={() => setConfirmDelete(true)}>
                  <Text type="bodySmallBold" color={theme.colors.error}>
                    {t('events.form.delete')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          ) : null}
        </ScrollView>
      </KeyboardAwareView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    scroll: { padding: theme.spacing.xl, alignItems: 'center', gap: theme.spacing.lg },
    row: { flexDirection: 'row', gap: theme.spacing.lg },
    footer: { width: '100%', maxWidth: 760, gap: theme.spacing.md },
    actions: { alignItems: 'flex-start' },
    danger: {
      width: '100%',
      maxWidth: 760,
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.xxl,
    },
  })
